import React, { useState, useEffect, useCallback } from "react";
import { Calendar, Modal, Form, Input, Select, DatePicker, Upload, Button, ConfigProvider } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { Clock, Briefcase, FileText, Download } from "lucide-react";
import dayjs from "dayjs";
import toast from "react-hot-toast";

import Sidebar from "./Sidebar";
import Footer from "./Footer";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import "../styles/myAttendance.css";

const { Option } = Select;

export default function MyAttendanceDashboard({ isModal = false }) {
  const { user, token } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  // Data states
  const [holidays, setHolidays] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(false);

  // Summary counts
  const [summary, setSummary] = useState({
    present: 0,
    absent: 0,
    leave: 0,
    halfDay: 0,
    regularized: 0,
  });

  // Current viewed month/year in calendar
  const [currentDate, setCurrentDate] = useState(dayjs());

  // Modal states
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [actionType, setActionType] = useState(null); // 'LEAVE' | 'REGULARIZE' | null

  // Forms
  const [regForm] = Form.useForm();
  const [leaveForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  // Fetch data
  const fetchData = useCallback(async (date) => {
    setLoading(true);
    try {
      const month = date.month() + 1;
      const year = date.year();

      // Fetch Holidays
      const holRes = await api.get('/api/holidays');
      if (holRes.data && holRes.data.success !== false) {
        setHolidays(holRes.data.holidays || holRes.data.data || holRes.data || []);
      }

      // Fetch Leaves for employee
      const empId = user?.employee_id || user?.employeeId || user?.empId || user?.id;
      if (empId) {
        const leaveRes = await api.get(`/api/leaves/employee/${empId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setLeaves(leaveRes.data?.data || leaveRes.data?.leaves || []);
      }

      // Fetch Attendance for month
      const attRes = await api.get(`/api/attendance-report/report?month=${month}&year=${year}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const empList = attRes.data?.data?.employees || [];
      const myAtt = empList.find(e => 
        String(e.empId || e.employeeId).toUpperCase() === String(empId).toUpperCase()
      );
      
      let presentCount = 0;
      let absentCount = 0;
      let regularizedCount = 0;
      let leaveCount = 0;
      let halfDayCount = 0;

      if (myAtt && myAtt.attendance) {
        setAttendance(Object.values(myAtt.attendance));
        
        // Compute summary based on attendance data
        Object.values(myAtt.attendance).forEach(day => {
          const status = String(day.status || day.status_value).toUpperCase();
          if (status === 'P') presentCount++;
          else if (status === 'A') absentCount++;
          else if (status === 'REGULARIZED') regularizedCount++;
          else if (status === 'HD') halfDayCount++;
        });
      } else {
        setAttendance([]);
      }
      
      // Update summary based on fetched leaves
      // Basic count implementation
      setSummary({
        present: presentCount,
        absent: absentCount,
        leave: leaveCount,
        halfDay: halfDayCount,
        regularized: regularizedCount
      });

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [user, token]);

  useEffect(() => {
    fetchData(currentDate);
  }, [currentDate, fetchData]);

  const onPanelChange = (date) => {
    setCurrentDate(date);
  };

  const onDateSelect = (date) => {
    setSelectedDate(date);
    setIsModalVisible(true);
    setActionType(null);
  };

  // Cell Render Logic
  const dateCellRender = (date) => {
    const dateStr = date.format('YYYY-MM-DD');
    const dayHolidays = holidays.filter(h => h.holiday_date?.startsWith(dateStr) || h.date?.startsWith(dateStr));
    const dayLeaves = leaves.filter(l => {
      const start = dayjs(l.start_date);
      const end = dayjs(l.end_date);
      return date.isSameOrAfter(start, 'day') && date.isSameOrBefore(end, 'day');
    });
    
    // Find attendance record
    const attRecord = attendance.find(a => a.date?.startsWith(dateStr));

    return (
      <div className="ma-cell-events">
        {dayHolidays.map((h, i) => (
          <div key={`h-${i}`} className="ma-event-badge ma-event-holiday">
            {h.name}
          </div>
        ))}
        {dayLeaves.map((l, i) => (
          <div key={`l-${i}`} className="ma-event-badge ma-event-leave">
            Leave ({l.status})
          </div>
        ))}
        {attRecord && (
          <>
            {String(attRecord.status || attRecord.status_value).toUpperCase() === 'P' && (
              <div className="ma-event-badge ma-event-present">Present</div>
            )}
            {String(attRecord.status || attRecord.status_value).toUpperCase() === 'A' && (
              <div className="ma-event-badge ma-event-absent">Absent</div>
            )}
            {attRecord.check_in_time && (
              <div className="ma-event-time">
                <Clock /> In: {attRecord.check_in_time}
              </div>
            )}
            {attRecord.check_out_time && (
              <div className="ma-event-time">
                <Clock /> Out: {attRecord.check_out_time}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // Form Handlers
  const handleRegularizeSubmit = async (values) => {
    setSubmitting(true);
    try {
      const empId = user?.employee_id || user?.employeeId || user?.empId || user?.id;
      const payload = {
        employee_id: empId,
        date: selectedDate.format("YYYY-MM-DD"),
        type: values.requestType,
        reason: values.reason,
        requested_time: `${values.checkIn ? values.checkIn.format("HH:mm") : ''} - ${values.checkOut ? values.checkOut.format("HH:mm") : ''}`
      };
      
      const res = await api.post('/api/attendance-requests', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data?.success) {
        toast.success("Regularization request submitted successfully");
        setIsModalVisible(false);
        regForm.resetFields();
      } else {
        toast.error(res.data?.message || "Failed to submit request");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error submitting request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={isModal ? "" : "page container"}>
      {!isModal && sidebarOpen && <div className="overlay" onClick={toggleSidebar}></div>}
      {!isModal && <Sidebar sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} />}
      
      <main className={isModal ? "" : "main"}>
        {!isModal && <div className="hamburger" onClick={toggleSidebar}>☰</div>}
        <div className="my-attendance-container">
          
          <header className="ma-header">
            <div className="ma-header-breadcrumbs">Attendance / My Attendance</div>
            <h1>My Attendance</h1>
          </header>

          <div className="ma-content-layout">
            <div className="ma-calendar-section">
              <div className="ma-legend">
                <div className="ma-legend-item"><div className="ma-summary-dot" style={{background: '#166534'}}></div> Present</div>
                <div className="ma-legend-item"><div className="ma-summary-dot" style={{background: '#991b1b'}}></div> Absent</div>
                <div className="ma-legend-item"><div className="ma-summary-dot" style={{background: '#1e40af'}}></div> Leave</div>
                <div className="ma-legend-item"><div className="ma-summary-dot" style={{background: '#854d0e'}}></div> Holiday</div>
                <div className="ma-legend-item"><div className="ma-summary-dot" style={{background: '#6b21a8'}}></div> Regularized</div>
              </div>
              
              <ConfigProvider theme={{ components: { Calendar: { fullBg: '#fff', itemActiveBg: '#e6f4ff' } } }}>
                <Calendar 
                  className="ma-calendar"
                  value={currentDate}
                  onPanelChange={onPanelChange}
                  onSelect={onDateSelect}
                  cellRender={dateCellRender}
                />
              </ConfigProvider>
            </div>

            <div className="ma-summary-section">
              <div className="ma-summary-card">
                <div className="ma-summary-title">Attendance Summary</div>
                <div className="ma-summary-list">
                  <div className="ma-summary-item">
                    <div className="ma-summary-label">
                      <div className="ma-summary-dot" style={{background: '#10B981'}}></div> Present
                    </div>
                    <div className="ma-summary-value" style={{color: '#10B981'}}>{summary.present}</div>
                  </div>
                  <div className="ma-summary-item">
                    <div className="ma-summary-label">
                      <div className="ma-summary-dot" style={{background: '#EF4444'}}></div> Absent
                    </div>
                    <div className="ma-summary-value" style={{color: '#EF4444'}}>{summary.absent}</div>
                  </div>
                  <div className="ma-summary-item">
                    <div className="ma-summary-label">
                      <div className="ma-summary-dot" style={{background: '#3B82F6'}}></div> Leave
                    </div>
                    <div className="ma-summary-value" style={{color: '#3B82F6'}}>{summary.leave}</div>
                  </div>
                  <div className="ma-summary-item">
                    <div className="ma-summary-label">
                      <div className="ma-summary-dot" style={{background: '#F59E0B'}}></div> Half Day
                    </div>
                    <div className="ma-summary-value" style={{color: '#F59E0B'}}>{summary.halfDay}</div>
                  </div>
                  <div className="ma-summary-item">
                    <div className="ma-summary-label">
                      <div className="ma-summary-dot" style={{background: '#8B5CF6'}}></div> Regularized
                    </div>
                    <div className="ma-summary-value" style={{color: '#8B5CF6'}}>{summary.regularized}</div>
                  </div>
                </div>
              </div>

              <div className="ma-summary-card" style={{padding: '16px'}}>
                <button className="ma-action-btn ma-btn-primary" onClick={() => {
                  setSelectedDate(dayjs());
                  setActionType('REGULARIZE');
                  setIsModalVisible(true);
                }}>
                  Regularize Attendance
                </button>
                <button className="ma-action-btn ma-btn-outline" onClick={() => {
                  setSelectedDate(dayjs());
                  setActionType('LEAVE');
                  setIsModalVisible(true);
                }}>
                  Apply Leave
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Action Modal */}
      <Modal
        title={actionType === 'REGULARIZE' ? "New Attendance Regularization Request" : actionType === 'LEAVE' ? "Apply Leave" : `Actions for ${selectedDate?.format('DD-MM-YYYY')}`}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setActionType(null);
          regForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        {!actionType && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '20px 0' }}>
            <Button size="large" type="primary" onClick={() => setActionType('REGULARIZE')}>Regularize Attendance</Button>
            <Button size="large" onClick={() => setActionType('LEAVE')}>Apply Leave</Button>
          </div>
        )}

        {actionType === 'REGULARIZE' && (
          <Form form={regForm} layout="vertical" onFinish={handleRegularizeSubmit} initialValues={{ date: selectedDate }}>
            <Form.Item name="date" label="Date" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" disabled />
            </Form.Item>
            
            <Form.Item name="requestType" label="Request Type" rules={[{ required: true, message: 'Please select a request type' }]}>
              <Select placeholder="Select request type">
                <Option value="Forgot Check-in">Forgot Check-in</Option>
                <Option value="Forgot Check-out">Forgot Check-out</Option>
                <Option value="Both Check-in & Check-out">Both Check-in & Check-out</Option>
                <Option value="Mark as Present">Mark as Present</Option>
              </Select>
            </Form.Item>

            <div style={{ display: 'flex', gap: '16px' }}>
              <Form.Item name="checkIn" label="Actual Check-in Time" style={{ flex: 1 }}>
                <DatePicker picker="time" format="HH:mm" style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="checkOut" label="Actual Check-out Time" style={{ flex: 1 }}>
                <DatePicker picker="time" format="HH:mm" style={{ width: '100%' }} />
              </Form.Item>
            </div>

            <Form.Item name="reason" label="Reason" rules={[{ required: true, message: 'Reason is required' }]}>
              <Input.TextArea rows={3} placeholder="Forgot to check-in due to client meeting." />
            </Form.Item>

            <Form.Item name="attachment" label="Attachment (Optional)">
              <Upload maxCount={1}>
                <Button icon={<UploadOutlined />}>Choose File</Button>
              </Upload>
              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>(e.g., meeting invite, travel proof)</div>
            </Form.Item>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <Button onClick={() => setIsModalVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={submitting}>Submit Request</Button>
            </div>
          </Form>
        )}

        {actionType === 'LEAVE' && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <p>You will be redirected to the full Leave Application form.</p>
            <Button type="primary" onClick={() => window.location.href = `/LeaveForm?date=${selectedDate?.format('YYYY-MM-DD')}`}>Proceed to Apply Leave</Button>
          </div>
        )}
      </Modal>

    </div>
  );
}
